import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { columns: "3" }
const _hoisted_2 = ["options", "value", "disabled", "isLoading"]
const _hoisted_3 = {
  key: 0,
  class: "error-message"
}
const _hoisted_4 = ["value", "placeholder", "disabled"]
const _hoisted_5 = ["value", "placeholder"]
const _hoisted_6 = ["value", "placeholder", "onTextInputChange"]
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { key: 0 }
const _hoisted_9 = { key: 1 }
const _hoisted_10 = { class: "env-var-section" }
const _hoisted_11 = { class: "env-var-title-row" }
const _hoisted_12 = { class: "env-var-data" }
const _hoisted_13 = ["value", "onTextInputChange"]
const _hoisted_14 = ["value", "onCodeInputChange"]
const _hoisted_15 = ["onClick"]
const _hoisted_16 = { class: "config-data-actions" }

import { ref, watch, computed, watchEffect} from 'vue';
import { useStore } from 'vuex';
import Banner from '@components/Banner/Banner.vue';
import ChartValues from '../settings/ChartValues.vue';
import { useNamespaces } from '../../queries/useNamespaceQueries';
import { useAppChart } from '../../queries/useAppChartsQueries';
import { ListResourceRequestParams, ResourceQueryOptions } from '../../models/resource/ui-types';
import { debounce } from 'lodash';
import { AppFormDetails, AppFormSource, AppFormBindings } from '../../models/application/ui-types';
import { ChartSetting } from '../../models/catalogservice/ui-types';


export default /*@__PURE__*/_defineComponent({
  __name: 'AppInfo',
  props: {
    details: {},
    mode: {},
    chart: {},
    source: {},
    active: { type: Boolean },
    updateDetails: { type: Function },
    updateChartSettings: { type: Function },
    updateBindings: { type: Function }
  },
  setup(__props: any) {

const store = useStore();

const t = store.getters['i18n/t'];

// Props
const props = __props;

// Reactive state
const bulkFileInput = ref<HTMLInputElement | null>(null);
const fileDialogActive = ref(false);

watch(() => props.chart, (newChart) => {
  appChartRequestOptions.value.enabled = !!newChart;
});
const selectedChart = computed(() => {
  return props.chart || '';
});

const appChartRequestOptions = ref<ResourceQueryOptions>({ enabled: false, polling: false });
const { data: appChart } = useAppChart(store, selectedChart, appChartRequestOptions);

// Watch for changes to the app chart and set default settings if none exist
watch(() => appChart.value?.meta.name, () => {
  if (appChart.value) {
    const defaultSettings: Record<string, string> = {};
    const chartSettings = appChart.value.settings || [];
    chartSettings.forEach((setting) => {
      defaultSettings[setting.name] = props.details.settings[setting.name] ?? setting.value;
    });
    props.updateChartSettings(chartSettings);
    props.updateDetails({ settings: defaultSettings });
  }
});

const namespaceRequestParams = ref<ListResourceRequestParams>({ page: 1, pageSize: 25, search: '' });
const namespaceRequestOptions = ref<ResourceQueryOptions>({ enabled: true, polling: false });
const {data: namespaces, isLoading: isLoadingNamespaces, isError: isErrorNamespaces } = useNamespaces(store, namespaceRequestParams, namespaceRequestOptions);

const namespaceOpts = computed(() => {
  return namespaces?.value?.items.map((ns) => ({ label: ns.meta.name, value: ns.meta.name })) || [];
});

const onNamespaceFilter = debounce((query: string) => {
  namespaceRequestParams.value.page = 1;
  namespaceRequestParams.value.search = query;
}, 500);

// Watch for changes to the active namespace cache and update the request params accordingly
watchEffect(() => {
  void store.state.activeNamespaceCacheKey;
  const activeNamespaces = store.state.activeNamespaceCache;

  if (activeNamespaces && Object.keys(activeNamespaces).length > 0) {
    namespaceRequestParams.value.namespaces = Object.keys(activeNamespaces);
  } else {
    namespaceRequestParams.value.namespaces = undefined;
  }
});

const isEdit = computed(() => props.mode === 'edit');

watch(() => props.active, (isActive) => {
  if (isActive && !props.details.name) {
    generateDefaultName();
  }
});

// Generate a default name for new applications
const generateDefaultName = () => {
  try {
    // Use source prop if available (create mode), otherwise try appSource (edit mode)
    const source = props.source;

    let baseName = '';

    // Determine base name from source
    if (source.type === 'github' || source.type === 'gitlab') {
      baseName = source[source.type]?.repository || '';
    } else if (source.type === 'gitUrl') {
      // Extract base name from git URL
      const urlParts = source.gitUrl?.url.split('/');
      baseName = urlParts?.length ? urlParts[urlParts.length - 1].replace(/\.git$/, '') : '';
    } else if (source.type === 'containerUrl') {
      // Extract base name from container URL
      const urlParts = source.containerUrl?.url.split('/');
      const imageWithTag = urlParts?.length ? urlParts[urlParts.length - 1] : '';
      baseName = imageWithTag.split(':')[0];
    }
    // Append random string to the end of the base name
    if (baseName) {
      const randomSuffix = Math.random().toString(36).substring(2, 8);
      props.updateDetails({name: `${baseName}-${randomSuffix}`.toLowerCase()});
    }
  } catch (e) {
    console.log(e);
    return '';
  }
};

// Allows us to move the checkbox at the top of the list so layout-wise looks better
const moveBooleansToFront = (settings: ChartSetting[]) => {
  if (!settings || !settings.length) {
    return [];
  }

  settings.sort((a, b) => {
    const aValue = a.type === 'bool' ? 0 : 1;
    const bValue = b.type === 'bool' ? 0 : 1;

    return aValue - bValue;
  });

  return settings;
};

// "Add from file", parse a KEY=VALUE file and add rows
function triggerBulkFileUpload() {
  fileDialogActive.value = true;
  bulkFileInput.value?.click();
}

// Parse a simple KEY=VALUE file, ignoring empty lines and comments (lines starting with #)
function onBulkFileChange(event: Event) {
  fileDialogActive.value = false;
  const file = (event.target as HTMLInputElement).files?.[0];

  if (!file) return;

  const reader = new FileReader();

  reader.onload = (e) => {
    const text = (e.target?.result as string) || '';
    const newRows: Array<{ key: string; value: string }> = [];

    text.split('\n').forEach((line) => {
      const trimmed = line.trim();

      if (!trimmed || trimmed.startsWith('#')) return;

      const sep = trimmed.indexOf('=');

      if (sep > 0) {
        newRows.push({ key: trimmed.slice(0, sep).trim(), value: trimmed.slice(sep + 1) });
      }
    });

    // If there are new rows, add them to the existing config data. If the existing data is just one empty row, replace it instead.
    if (newRows.length) {
      const existing = props.details.environment;
      const onlyEmptyRow = existing.length === 1 && !existing[0].key && !existing[0].value;

      props.updateDetails({ environment: onlyEmptyRow ? newRows : [...existing, ...newRows] });
    }
  };
  reader.readAsText(file);
  (event.target as HTMLInputElement).value = '';
}


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("trailhand-form-card", null, [
    _createElementVNode("trailhand-form-row", _hoisted_1, [
      _createElementVNode("div", null, [
        _createElementVNode("trailhand-dropdown", {
          style: {"width":"100%"},
          options: namespaceOpts.value,
          value: _ctx.details.namespace,
          label: "Namespace",
          placeholder: "Select a namespace",
          disabled: isEdit.value,
          required: "",
          filterable: "",
          isLoading: _unref(isLoadingNamespaces),
          onDropdownChange: _cache[0] || (_cache[0] = (e) => {
            _ctx.updateDetails({ namespace: e.detail.value });
            // Reset the bindings when the namespace changes
            _ctx.updateBindings({ services: [], configurations: [] });
          }),
          onDropdownFilter: _cache[1] || (_cache[1] = (e) => { _unref(onNamespaceFilter)(e.detail.filter); })
        }, null, 40, _hoisted_2),
        _cache[8] || (_cache[8] = _createTextVNode()),
        (_unref(isErrorNamespaces))
          ? (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(_unref(t)(`epinio.namespace.errors.fetchAll`)), 1))
          : _createCommentVNode("", true)
      ]),
      _cache[9] || (_cache[9] = _createTextVNode()),
      _createElementVNode("trailhand-text-input", {
        value: _ctx.details.name,
        "data-testid": "epinio_app-info_name",
        label: "Name",
        placeholder: _unref(t)('epinio.applications.create.namePlaceholder'),
        disabled: isEdit.value,
        required: "",
        onTextInputChange: _cache[2] || (_cache[2] = (e) => _ctx.updateDetails({ name: e.detail.value }))
      }, null, 40, _hoisted_4),
      _cache[10] || (_cache[10] = _createTextVNode()),
      _createElementVNode("trailhand-text-input", {
        value: _ctx.details.instances,
        "data-testid": "epinio_app-info_instances",
        label: "Instances",
        placeholder: _unref(t)('epinio.applications.create.instancesPlaceholder'),
        required: "",
        type: "number",
        min: "0",
        onTextInputChange: _cache[3] || (_cache[3] = (e) => _ctx.updateDetails({ instances: e.detail.value }))
      }, null, 40, _hoisted_5)
    ]),
    _cache[21] || (_cache[21] = _createTextVNode()),
    _createElementVNode("div", null, [
      _cache[12] || (_cache[12] = _createElementVNode("h3", null, "Routes", -1)),
      _cache[13] || (_cache[13] = _createTextVNode()),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.details.routes, (route, index) => {
        return (_openBlock(), _createElementBlock("div", {
          key: index,
          class: "route-item"
        }, [
          _createElementVNode("trailhand-text-input", {
            style: {"flex":"1"},
            value: route,
            placeholder: _unref(t)('epinio.applications.create.routes.placeholder'),
            onTextInputChange: (e) => { _ctx.updateDetails({ routes: _ctx.details.routes.map((r, i) => i === index ? e.detail.value : r) }); }
          }, null, 40, _hoisted_6),
          _cache[11] || (_cache[11] = _createTextVNode()),
          (props.mode !== 'view')
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                class: "remove-link",
                onClick: () => { _ctx.updateDetails({ routes: _ctx.details.routes.filter((_, i) => i !== index) }); }
              }, "\n          Remove\n        ", 8, _hoisted_7))
            : _createCommentVNode("", true)
        ]))
      }), 128)),
      _cache[14] || (_cache[14] = _createTextVNode()),
      (props.mode !== 'view')
        ? (_openBlock(), _createElementBlock("trailhand-button", {
            key: 0,
            variant: "alternate",
            onButtonClick: _cache[4] || (_cache[4] = () => { _ctx.updateDetails({ routes: [..._ctx.details.routes, ''] }); })
          }, "\n        Add Row\n      ", 32))
        : _createCommentVNode("", true)
    ]),
    _cache[22] || (_cache[22] = _createTextVNode()),
    (isEdit.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
          _createVNode(Banner, { color: "info" }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)('epinio.applications.create.settingsVars.description')), 1)
            ]),
            _: 1
          })
        ]))
      : _createCommentVNode("", true),
    _cache[23] || (_cache[23] = _createTextVNode()),
    (_unref(appChart)?.settings)
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          _createVNode(ChartValues, {
            value: _ctx.details.settings,
            "onUpdate:value": _cache[5] || (_cache[5] = ($event: any) => ((_ctx.details.settings) = $event)),
            chart: moveBooleansToFront(_unref(appChart)?.settings),
            title: _unref(t)('epinio.applications.create.settingsVars.title'),
            mode: props.mode,
            disabled: false
          }, null, 8, ["value", "chart", "title", "mode"])
        ]))
      : _createCommentVNode("", true),
    _cache[24] || (_cache[24] = _createTextVNode()),
    _createElementVNode("div", _hoisted_10, [
      _createElementVNode("div", _hoisted_11, [
        _createElementVNode("h3", null, _toDisplayString(_unref(t)('epinio.applications.create.envvar.title')), 1)
      ]),
      _cache[20] || (_cache[20] = _createTextVNode()),
      _createElementVNode("div", _hoisted_12, [
        (_ctx.details.environment.length > 0 || isEdit.value)
          ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(_ctx.details.environment, (envVar, i) => {
              return (_openBlock(), _createElementBlock("div", {
                key: i,
                class: "env-var-row"
              }, [
                _createElementVNode("trailhand-text-input", {
                  style: {"flex":"1"},
                  value: envVar.key,
                  label: "Key",
                  required: "",
                  placeholder: "e.g. foo",
                  onTextInputChange: (e) => _ctx.updateDetails({ environment: _ctx.details.environment.map((env, index) => index === i ? { ...env, key: e.detail.value } : {...env}) })
                }, null, 40, _hoisted_13),
                _cache[15] || (_cache[15] = _createTextVNode()),
                _createElementVNode("trailhand-code-editor", {
                  style: {"flex":"1"},
                  value: envVar.value,
                  label: "Value",
                  required: "",
                  onCodeInputChange: (e) => _ctx.updateDetails({ environment: _ctx.details.environment.map((env, index) => index === i ? { ...env, value: e.detail.value } : {...env}) })
                }, null, 40, _hoisted_14),
                _cache[16] || (_cache[16] = _createTextVNode()),
                _createElementVNode("button", {
                  class: "remove-link",
                  onClick: ($event: any) => (_ctx.updateDetails({ environment: _ctx.details.environment.filter((_, index) => index !== i) }))
                }, "\n              Remove\n            ", 8, _hoisted_15)
              ]))
            }), 128))
          : _createCommentVNode("", true),
        _cache[19] || (_cache[19] = _createTextVNode()),
        _createElementVNode("div", _hoisted_16, [
          _createElementVNode("trailhand-button", {
            variant: "alternate",
            onButtonClick: _cache[6] || (_cache[6] = ($event: any) => (_ctx.updateDetails({ environment: [..._ctx.details.environment, { key: '', value: '' }] })))
          }, "\n            Add\n          ", 32),
          _cache[17] || (_cache[17] = _createTextVNode()),
          _createElementVNode("trailhand-button", {
            variant: "alternate",
            onButtonClick: triggerBulkFileUpload
          }, "\n            Read From File\n          ", 32),
          _cache[18] || (_cache[18] = _createTextVNode()),
          _createElementVNode("input", {
            ref_key: "bulkFileInput",
            ref: bulkFileInput,
            type: "file",
            class: "hidden-file-input",
            onChange: onBulkFileChange,
            onCancel: _cache[7] || (_cache[7] = ($event: any) => (fileDialogActive.value = false))
          }, null, 544)
        ])
      ])
    ])
  ]))
}
}

})